import { without, reject, eqProps, propOr } from "ramda";
import * as types from "store/scheduler/schedulerTypes";

const moderatorColors = [
  { hexCode: "#503891", className: "lavender-purple" },
  { hexCode: "#48A8A8", className: "hippie-blue" },
  { hexCode: "#888888", className: "grey" },
  { hexCode: "#FF7D59", className: "coral" },
  { hexCode: "#AAAAAA", className: "silver-chalice" },
  { hexCode: "#7B679E", className: "kimberly" },
  { hexCode: "#008A89", className: "teal" },
  { hexCode: "#FDB321", className: "my-sin" },
  { hexCode: "#E5562E", className: "cinnabar" },
  { hexCode: "#666666", className: "dove-gray" },
  { hexCode: "#009B7C", className: "light-green" },
];

const initialState = {
  availability: [],
  selectedSlotId: 0,
  selectedSlotData: null,
  timeSlots: [],
  moderatorTimeSlots: [],
  possibleModeratorsById: {},
  observerEmails: [],
  observerEmailsValid: true,
  pendingModeratorIdToAdd: [],
  pendingModeratorIdsToDelete: [],
  isLoading: false,
  filteredItemsById: new Set(),
  moderatorColorsById: {},
  selectedAvailabilityId: 0,
  timeSlotPopOverisOpen: false,
  availabilityPopOverisOpen: false,
  conferenceLink: "",
  filterBy: "Projects",
  filteredProjects: new Set(),
  filteredModerators: new Set(),
  singleModerator: {
    id: -1,
    label: "Select a moderator",
    clientId: 0,
    firstName: "",
  },
  importModeratorAvOpen: false,
  unlinkImportModeratorOpen: false,
  importAvStatus: "",
  isLoadingAv: false,
  unlinkIsLoading: false,
  isCalendarExportedBefore: false,
  isForcedLoading: false,
  externalCalendar: "",
  externalCalendarLink: "",
  showURLAlreadyUsedError: false,
  moderatorName: "",
  timeslotModalChosenAction: "",
  invalidateReschedule: false,
  invalidateSelectedFromCompleted: undefined,
};

const toggleItemId = (filteredItemsById, id) => {
  if (filteredItemsById.has(id)) {
    filteredItemsById.delete(id);
  } else {
    return filteredItemsById.add(id);
  }

  return filteredItemsById;
};

const handleAddModerator = (state, payload) =>
  state.pendingModeratorIdsToDelete.includes(payload.moderatorId)
    ? {
        pendingModeratorIdsToDelete: without(
          [payload.moderatorId],
          state.pendingModeratorIdsToDelete,
        ),
      }
    : {
        pendingModeratorIdToAdd: [
          ...state.pendingModeratorIdToAdd,
          payload.moderatorId,
        ],
      };

const parserModeratorColors = (calendarEvents = []) =>
  calendarEvents.reduce(
    (accum, event) => ({
      ...accum,
      [event.moderatorId]:
        accum[event.moderatorId] || // check if there's a color already assigned
        moderatorColors[Object.keys(accum).length] || // grab the next unused color
        moderatorColors[0], // defult to purple
    }),
    {},
  );

const handleDeleteModerator = (state, payload) =>
  state.pendingModeratorIdToAdd.includes(payload)
    ? {
        pendingModeratorIdToAdd: without(
          [payload],
          state.pendingModeratorIdToAdd,
        ),
      }
    : {
        pendingModeratorIdsToDelete: [
          ...state.pendingModeratorIdsToDelete,
          payload,
        ],
      };

const schedulerReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case types.FETCH_IMPORT_AVAILABILITY_STATUS_REQUESTED:
      return {
        ...state,
        ...payload,
        isLoadingImpAv: true,
        isForcedLoading: payload.isForced,
      };
    case types.UNLINK_SUCCEEDED:
      return {
        ...state,
        ...payload,
        isLoadingImpAv: true,
        isForcedLoading: payload.isForced,
      };
    case types.UPDATE_GOOGLE_SPREAD_SHEET_SUCCEEDED:
    case types.UPDATE_GOOGLE_SPREAD_SHEET_FAILED:
      return { ...state, downloadGoogleSheetLoading: false };

    case types.UPDATE_GOOGLE_SPREAD_SHEET:
      return { ...state, downloadGoogleSheetLoading: true };
    case types.FETCH_IMPORT_AVAILABILITY_STATUS_SUCCEEDED:
      return {
        ...state,
        ...payload,
        isLoadingImpAv: false,
        isCalendarExportedBefore: payload.isCalendarExportedBefore,
        isForcedLoading: false,
        externalCalendar: payload.external_calendar,
        externalCalendarKey: payload.external_calendar_key,
        modifiedOn: payload.modifiedOn,
        canNotRunImport: payload.canNotRunImport,
      };
    case types.FETCH_IMPORT_AVAILABILITY_STATUS_FAILED:
      return { ...state, ...payload, isLoadingImpAv: false };
    case types.FETCH_MODERATOR_AVAILABILITY_REQUESTED:
      return {
        ...state,
        ...payload,
        allowAddAv: false,
        availability: payload && payload.isImported ? state.availability : [],
        isLoading: true,
      };
    case types.FETCH_MODERATOR_AVAILABILITY_FAILED:
      return { ...state, ...payload, allowAddAv: true, isLoading: false };
    case types.FETCH_MODERATOR_AVAILABILITY_SUCCEEDED:
      const calendarEvents = payload.availability.concat(payload.timeSlots);
      return {
        ...state,
        ...payload,
        moderatorColorsById: parserModeratorColors(calendarEvents),
        allowAddAv: true,
        isLoading: false,
      };
    case types.POST_MODERATOR_FOR_CONFERENCE_REQUESTED:
      return { ...state, ...handleAddModerator(state, payload) };
    case types.DELETE_MODERATOR_FROM_CONFERENCE_REQUESTED:
      return { ...state, ...handleDeleteModerator(state, payload) };
    /**
     * TODO - consider changing PUT / POST response to the affected object
     * rather than an array of availabilities for that moderator unless there
     * is some reason for the unconventional response
     */

    case types.POST_MODERATOR_AVAILABILITY_REQUESTED:
      return { ...state, allowAddAv: false };
    case types.POST_MODERATOR_AVAILABILITY_FAILED:
      return { ...state, allowAddAv: true };
    case types.POST_MODERATOR_AVAILABILITY_SUCCEEDED:
    case types.PUT_MODERATOR_AVAILABILITY_SUCCEEDED: {
      const filteredAvailability = reject(
        eqProps("moderatorId", payload),
        state.availability,
      );
      return {
        ...state,
        availability: [...filteredAvailability, ...payload.availability],
        isLoading: false,
        selectedAvailabilityId: 0,
        allowAddAv: true,
      };
    }
    case types.TOGGLE_IMPORT_MODERATOR_AVAILABILITY_MODAL: {
      return {
        ...state,
        importModeratorAvOpen: !state.importModeratorAvOpen,
      };
    }
    case types.TOGGLE_UNLINK_MODERATOR_AVAILABILITY_MODAL: {
      return {
        ...state,
        unlinkImportModeratorOpen: !state.unlinkImportModeratorOpen,
      };
    }
    case types.DELETE_MODERATOR_AVAILABILITY_SUCCEEDED: {
      return {
        ...state,
        availability: reject(
          eqProps("availabilityId", payload),
          state.availability,
        ),
        isLoading: false,
        selectedAvailabilityId: 0,
        availabilityPopOverisOpen: false,
      };
    }
    case types.TOGGLE_TIMESLOT_POPOVER:
      return !state.timeSlotModalOpen
        ? {
            ...state,
            selectedAvailabilityId: 0,
            selectedSlotId: propOr(null, "timeslotId", payload),
            selectedVirtualId: propOr(null, "virtualId", payload),
            timeSlotPopOverisOpen:
              state.warningModalOpen === true
                ? false
                : !state.timeSlotPopOverisOpen,
          }
        : {
            ...state,
            timeSlotPopOverisOpen:
              state.warningModalOpen === true
                ? false
                : !state.timeSlotPopOverisOpen,
          };
    case types.CLOSE_TIMESLOT_POPOVER:
      return {
        ...state,
        timeSlotPopOverisOpen: false,
        // Preserve selectedSlotId if the modal is open, so downstream fetches don't lose context
        selectedSlotId: state.timeSlotModalOpen ? state.selectedSlotId : 0,
      };
    case types.TOGGLE_FILTER:
      return {
        ...state,
        filterBy: payload.filterBy,
        filteredItemsById: new Set(),
      };
    case types.SET_SCHEDULER_TIMEZONE:
      return {
        ...state,
        selectedTimeZone: payload,
      };
    case types.TOGGLE_ALLOW_ADD_AV:
      return {
        ...state,
        allowAddAv: !state.allowAddAv,
      };
    case types.TOGGLE_TIMESLOT_MODAL:
      return {
        ...state,
        timeSlotModalOpen: !state.timeSlotModalOpen,
        pendingModeratorIdToAdd: [],
        pendingModeratorIdsToDelete: [],
        selectedSlotId: !state.timeSlotModalOpen
          ? propOr(state.selectedSlotId, "timeSlotId", payload)
          : 0,
        selectedSlotData: !state.timeSlotModalOpen
          ? propOr(state.selectedSlotData, "slot", payload)
          : null,
        timeslotModalChosenAction: !state.timeSlotModalOpen
          ? propOr(state.timeslotModalChosenAction, "chosenAction", payload)
          : "",
        invalidateReschedule: !state.timeSlotModalOpen
          ? Boolean(propOr(false, "invalidateReschedule", payload))
          : false,
        invalidateSelectedFromCompleted: !state.timeSlotModalOpen
          ? propOr(
              state.invalidateSelectedFromCompleted,
              "invalidateSelectedFromCompleted",
              payload,
            )
          : undefined,
        observerEmails: [],
      };
    case types.TOGGLE_IMP_AVA_WARNING_MODAL:
      return {
        ...state,
        warningModalOpen: !state.warningModalOpen,
        pendingModeratorIdToAdd: [],
        pendingModeratorIdsToDelete: [],
        selectedSlotId: !state.warningModalOpen
          ? propOr(state.selectedSlotId, "timeSlotId", payload)
          : 0,
        selectedSlotData: !state.warningModalOpen
          ? propOr(state.selectedSlotData, "slot", payload)
          : null,
        observerEmails: [],
      };
    case types.SET_RESPONDENT_LANGUAGE:
      return {
        ...state,
        language: payload.language,
      };
    case types.FETCHING_MODERATORS:
    case types.FETCH_POSSIBLE_MODERATORS_FOR_TIME_SLOT_REQUESTED:
    case types.PUT_UPDATED_TIMESLOT_INFO_REQUESTED:
      return { ...state, isLoading: true };
    case types.FETCH_MODERATORS_FOR_TIME_SLOT_SUCCEEDED:
      return { ...state, ...payload };
    case types.POST_MODERATOR_FOR_CONFERENCE_SUCCEEDED:
    case types.DELETE_MODERATOR_FROM_CONFERENCE_SUCCEEDED:
    case types.PUT_UPDATED_TIMESLOT_INFO_SUCCEEDED:
      return {
        ...state,
        ...payload,
        isLoading: false,
        selectedSlotId: 0,
        observerEmails: [],
      };
    case types.FETCH_POSSIBLE_MODERATORS_FOR_TIME_SLOT_SUCCEEDED:
    case types.TOGGLE_TIMESLOT_MODAL_FROM_SCHEDULER_SUCCEEDED:
    case types.FETCH_AVAILABILITY_FOR_PROJECT_SUCCEEDED:
      return { ...state, ...payload, isLoading: false };
    case types.FETCH_AVAILABILITY_AND_TIME_SLOTS_SUCCEEDED: {
      const calendarEvents = payload.availability.concat(payload.timeSlots);
      return {
        ...state,
        ...payload,
        moderatorColorsById: parserModeratorColors(calendarEvents),
      };
    }
    case types.FILTER_SCHEDULE_REQUESTED:
      let filteredItemsById;
      let filteredModerators;
      let filteredProjects;
      if (payload) {
        filteredItemsById = toggleItemId(state.filteredItemsById, payload);
        if (state.filterBy === "Moderators") {
          filteredModerators = toggleItemId(state.filteredModerators, payload);
          filteredProjects = state.filteredProjects;
        } else if (state.filterBy === "Projects") {
          filteredProjects = toggleItemId(state.filteredProjects, payload);
          filteredModerators = state.filteredModerators;
        }
      } else {
        filteredItemsById = new Set();
        if (state.filterBy === "Moderators") {
          filteredModerators = new Set();
          filteredProjects = state.filteredProjects;
        } else if (state.filterBy === "Projects") {
          filteredProjects = new Set();
          filteredModerators = state.filteredModerators;
        }
      }
      return {
        ...state,
        filteredItemsById: filteredItemsById,
        filteredModerators: filteredModerators,
        filteredProjects: filteredProjects,
      };
    case types.TOGGLE_AVAILABILITY_POPOVER:
      return {
        ...state,
        selectedSlotId: 0,
        selectedAvailabilityId: propOr(null, "id", payload),
        selectedVirtualId: propOr(null, "virtualId", payload),
        availabilityPopOverisOpen: !state.availabilityPopOverisOpen,
      };
    case types.RESET_MODERATOR_STATE:
      return { ...initialState, filteredItemsById: new Set() };
    case types.FETCH_CONFERENCE_LINK_BY_TIMESLOT_SUCCEEDED:
      return { ...state, ...payload, conferenceLink: payload.conferenceLink };
    case types.SET_SINGLE_MODERATOR:
      return {
        ...state,
        singleModerator: {
          id: payload.id,
          label: payload.label,
          clientId: payload.clientId,
          firstName: payload.firstName,
        },
      };
    case types.SET_FILTERED_PROJECTS:
      return { ...state, filterBy: "Projects", filteredProjects: payload };
    case types.START_FETCH_IMPORTED_MOD_AV_REQUESTED:
      return { ...state, isLoadingAv: true };
    case types.START_UNLINK_IMP_MOD:
      return { ...state, unlinkIsLoading: true };
    case types.FINISH_UNLINK_IMP_MOD:
      return { ...state, unlinkIsLoading: false };
    case types.START_FETCH_IMPORTED_MOD_AV_SUCCEEDED:
      return { ...state, isLoadingAv: false, importAvStatus: "In Progress" };
    case types.START_FETCH_IMPORTED_MOD_AV_FAILED:
      return { ...state, isLoadingAv: false };
    case types.SET_CALENDAR_URL_ALREADY_USED_ERROR:
      return {
        ...state,
        showURLAlreadyUsedError: payload ? payload.value : true,
        moderatorName: payload.moderatorName,
      };
    default:
      return state;
  }
};

export default schedulerReducer;
